import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { NotificationsService } from '../../modules/notifications/notifications.service';
import { IApiInvoice } from '../../modules/graphql/types/types';
import { InvoiceService } from './invoice.service';

/** Resubmit, resubmit group and delete of an invoice, shared by every list that shows invoices. Each one emits once it is done, so the caller can refresh. */
@Injectable()
export class InvoiceActionsService {

  constructor(
    private invoiceService: InvoiceService,
    private notifications: NotificationsService
  ) { }

  public resubmit(invoice: IApiInvoice): Observable<boolean> {
    const message = invoice?.splitGroupId
      ? 'This invoice is part of a split. Only this invoice will be re-sent.'
      : 'Are you sure this invoice is ready for resubmission?';

    return this.notifications.kendoConfirm(message).pipe(
      filter(v => !!v),
      switchMap(() => this.invoiceService.resubmit(invoice.id)),
      this.notifications.snackbarErrorPipe(),
      this.notifications.snackbarPipe('Invoice resubmitted!')
    );
  }

  public resubmitGroup(invoice: IApiInvoice): Observable<boolean> {
    return this.notifications.kendoConfirm('Re-send every invoice of this split that is not in Sage yet?').pipe(
      filter(v => !!v),
      switchMap(() => this.invoiceService.resubmitGroup(invoice.splitGroupId)),
      this.notifications.snackbarErrorPipe(),
      this.notifications.snackbarPipe('Split invoices resubmitted!')
    );
  }

  public remove(invoice: IApiInvoice): Observable<boolean> {
    if (!invoice?.splitGroupId) {
      return this.notifications.kendoConfirm("Are you sure you want to delete this invoice? It won't delete it from SAGE").pipe(
        filter(v => !!v),
        switchMap(() => this.invoiceService.remove(invoice.id)),
        this.notifications.snackbarErrorPipe(),
        this.notifications.snackbarPipe('Invoice deleted.')
      );
    }

    // the API deletes the whole split, so list every invoice of it in the confirmation
    return this.invoiceService.getById(invoice.id).pipe(
      switchMap((found) => {
        const invoices = found?.SplitInvoices?.length ? found.SplitInvoices : [found];
        return this.notifications.kendoConfirm(
          `All ${invoices.length} invoices of this split (${invoices.map(({ invoiceNumber }) => invoiceNumber).join(', ')}) will be deleted from Extranet. They will not be deleted from SAGE, you have to delete them from SAGE manually.`
        ).pipe(
          filter(v => !!v),
          switchMap(() => this.invoiceService.remove(invoice.id)),
          this.notifications.snackbarPipe(`${invoices.length} invoices deleted.`)
        );
      }),
      this.notifications.snackbarErrorPipe()
    );
  }
}
